import {
  useState,
  useEffect,
  useRef,
  useCallback,
  startTransition,
} from "react";
import { useSearchParams } from "react-router-dom";
import Footer from "../components/Footer/Footer";
import ProductHeader from "../components/Products/ProductHeader";
import ProductFilterPanel from "../components/Products/ProductFilterPanel";
import type { FilterValues } from "../components/Products/ProductFilterPanel";
import MobileFilterDrawer from "../components/Products/MobileFilterDrawer";
import ActiveFilters from "../components/Products/ActiveFilters";
import { ProductGrid } from "../components/_ResponsiveComponents";
import {
  useInfiniteProducts,
  useProductSearch,
  usePriceRange,
  useBrands,
  useProductTypes,
  type ProductSortType,
} from "../hooks/products";
import { useCategoriesWithProducts } from "../hooks/useSupabase";
import Header from "../components/Header/Header";
import ScrollToTopButton from "../components/ScrollToTopButton";
import SEO from "../components/SEO/SEO";

type TViewMode = "grid" | "list";

// Default filter values
const DEFAULT_FILTERS: FilterValues = {
  priceRange: { min: undefined, max: undefined },
  brands: [],
  productTypes: [],
  rating: null,
  inStock: false,
  onSale: false,
  freeShipping: false,
};

const PAGE_SIZE = 20;

const DynamicProductPage = () => {
  // Get URL parameters first
  const [searchParams, setSearchParams] = useSearchParams();

  // Track if we're updating from code (not from URL navigation)
  const isUpdatingFromCode = useRef(false);

  // Initialize state from URL parameters immediately
  const categoryId = searchParams.get("category_id");
  const sort = searchParams.get("sort");
  const onSale = searchParams.get("on_sale");
  const search = searchParams.get("search");

  const [selectedCategory, setSelectedCategory] = useState<number | null>(
    categoryId ? parseInt(categoryId) : null,
  );
  const [sortBy, setSortBy] = useState<ProductSortType>(
    (sort as ProductSortType) || (onSale === "true" ? "popular" : "newest"),
  );
  const [viewMode, setViewMode] = useState<TViewMode>("grid");
  const [searchQuery, setSearchQuery] = useState<string | null>(search || null);

  // Filter state
  const [filters, setFilters] = useState<FilterValues>(DEFAULT_FILTERS);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Get all available brands
  const { brands } = useBrands();

  // Seed brand filter from URL ?brand_id= once brands load
  const brandIdParam = searchParams.get("brand_id");
  const brandSeededRef = useRef(false);
  useEffect(() => {
    if (brandSeededRef.current || !brandIdParam || brands.length === 0) return;
    const match = brands.find((b) => b.id.toString() === brandIdParam);
    if (match) {
      brandSeededRef.current = true;
      setFilters((prev) => ({ ...prev, brands: [match.name] }));
    }
  }, [brandIdParam, brands]);

  const availableBrands = brands.map((b) => b.name);
  const brandNameToIdMap = new Map(
    brands.map((b) => [b.name, b.id.toString()]),
  );

  const getBrandIdsFromNames = (brandNames: string[]) => {
    const ids = brandNames
      .map((name) => brandNameToIdMap.get(name))
      .filter(Boolean) as string[];
    return ids;
  };

  // Get all available product types
  const { productTypes } = useProductTypes();
  const availableProductTypes = productTypes.map((t) => t.name);
  const productTypeNameToIdMap = new Map(
    productTypes.map((t) => [t.name, t.id.toString()]),
  );

  const getProductTypeIdsFromNames = (names: string[]) => {
    return names
      .map((name) => productTypeNameToIdMap.get(name))
      .filter(Boolean) as string[];
  };

  // Determine if we should search or filter by category
  const isSearching = !!searchQuery && searchQuery.trim().length > 0;

  // Use search hook when searching
  const searchResults = useProductSearch({
    query: searchQuery || "",
    limit: PAGE_SIZE,
    filters: {
      categoryId: selectedCategory || undefined,
      priceRange: filters.priceRange,
      brands:
        filters.brands.length > 0
          ? getBrandIdsFromNames(filters.brands)
          : undefined,
      productTypes:
        filters.productTypes.length > 0
          ? getProductTypeIdsFromNames(filters.productTypes)
          : undefined,
    },
    enabled: isSearching,
  });

  // Use infinite products when NOT searching
  const infiniteResults = useInfiniteProducts({
    categoryId: selectedCategory || undefined,
    sortType: sortBy,
    pageSize: PAGE_SIZE,
    filters: {
      isOnSale: filters.onSale,
      priceRange: filters.priceRange,
      brands:
        filters.brands.length > 0
          ? getBrandIdsFromNames(filters.brands)
          : undefined,
      productTypes:
        filters.productTypes.length > 0
          ? getProductTypeIdsFromNames(filters.productTypes)
          : undefined,
    },
    enabled: !isSearching,
    maxPages: 5, // Keep at most 5 pages in memory (~100 products)
  });

  // Get global price range
  const { priceRange } = usePriceRange({
    categoryId: selectedCategory || undefined,
    searchQuery: searchQuery || undefined,
  });

  // Use active results based on search state
  const products = isSearching
    ? searchResults.results
    : infiniteResults.products;
  const totalResults = isSearching
    ? searchResults.totalCount || 0
    : infiniteResults.totalCount || 0;
  const loading = isSearching
    ? searchResults.isLoading
    : infiniteResults.isLoading;
  const error = isSearching
    ? searchResults.error?.message || null
    : infiniteResults.error?.message || null;

  // Get nested categories structure for breadcrumb
  const { categoriesWithSubs: nestedCategories } = useCategoriesWithProducts();

  const categories = nestedCategories || [];

  // Build full category breadcrumb trail
  const buildCategoryBreadcrumb = useCallback(() => {
    if (!selectedCategory) return [];

    const buildPath = (
      categoryId: number,
      allCategories: any[],
      path: Array<{ id: number; name: string }> = [],
    ): Array<{ id: number; name: string }> | null => {
      for (const cat of allCategories) {
        const newPath = [...path, { id: cat.id, name: cat.name }];

        if (cat.id === categoryId) {
          return newPath;
        }

        if (cat.sub_categories && cat.sub_categories.length > 0) {
          const found = buildPath(categoryId, cat.sub_categories, newPath);
          if (found) return found;
        }
      }
      return null;
    };

    const path = buildPath(selectedCategory, categories);
    return path || [];
  }, [selectedCategory, categories]);

  const categoryBreadcrumb = buildCategoryBreadcrumb();

  // Function to update URL parameters
  const updateURLParams = useCallback(
    (
      categoryId: number | null,
      sortValue?: string,
      searchValue?: string | null,
    ) => {
      isUpdatingFromCode.current = true;

      const params = new URLSearchParams();
      if (categoryId) params.set("category_id", categoryId.toString());
      if (sortValue) params.set("sort", sortValue);
      if (searchValue && searchValue.trim()) params.set("search", searchValue);
      setSearchParams(params, { replace: true });

      setTimeout(() => {
        isUpdatingFromCode.current = false;
      }, 0);
    },
    [setSearchParams],
  );

  // Sync state from URL navigation (back/forward/direct link)
  useEffect(() => {
    if (isUpdatingFromCode.current) return;

    const newCategoryId = searchParams.get("category_id");
    const newSort = searchParams.get("sort");
    const newOnSale = searchParams.get("on_sale");
    const newSearch = searchParams.get("search");

    const targetCategory = newCategoryId ? parseInt(newCategoryId) : null;
    const targetSort =
      (newSort as ProductSortType) ||
      (newOnSale === "true" ? "popular" : "newest");
    const targetSearch = newSearch || null;

    if (
      selectedCategory !== targetCategory ||
      sortBy !== targetSort ||
      searchQuery !== targetSearch
    ) {
      startTransition(() => {
        setSelectedCategory(targetCategory);
        setSortBy(targetSort);
        setSearchQuery(targetSearch);
      });
    }
  }, [searchParams, selectedCategory, sortBy, searchQuery]);

  // Handle sort change
  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy as ProductSortType);
    updateURLParams(selectedCategory, newSortBy, searchQuery);
  };

  // Handle view mode change
  const handleViewModeChange = (newViewMode: TViewMode) => {
    setViewMode(newViewMode);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery(null);
    updateURLParams(selectedCategory, sortBy, null);
  };

  // Filter handlers
  const handleFilterChange = (newFilters: FilterValues) => {
    setFilters(newFilters);
  };

  // Use global price range from hook
  const priceMin = priceRange?.min ?? 0;
  const priceMax = priceRange?.max ?? 1000;

  const handleRemoveFilter = (filterType: string, value?: string) => {
    if (filterType === "all") {
      setFilters({
        ...DEFAULT_FILTERS,
        priceRange: { min: undefined, max: undefined },
      });
      return;
    }

    const newFilters = { ...filters };

    switch (filterType) {
      case "price":
        newFilters.priceRange = { min: undefined, max: undefined };
        break;
      case "brand":
        if (value) {
          newFilters.brands = newFilters.brands.filter((b) => b !== value);
        }
        break;
      case "productType":
        if (value) {
          newFilters.productTypes = newFilters.productTypes.filter(
            (t) => t !== value,
          );
        }
        break;
      case "rating":
        newFilters.rating = null;
        break;
      case "inStock":
        newFilters.inStock = false;
        break;
      case "onSale":
        newFilters.onSale = false;
        break;
      case "freeShipping":
        newFilters.freeShipping = false;
        break;
    }

    setFilters(newFilters);
  };

  const handleClearAllFilters = () => {
    setFilters({
      ...DEFAULT_FILTERS,
      priceRange: { min: undefined, max: undefined },
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (
      (filters.priceRange.min !== undefined &&
        filters.priceRange.min !== priceMin) ||
      (filters.priceRange.max !== undefined &&
        filters.priceRange.max !== priceMax)
    )
      count++;
    if (filters.brands.length > 0) count += filters.brands.length;
    if (filters.rating !== null) count++;
    if (filters.inStock) count++;
    if (filters.onSale) count++;
    if (filters.freeShipping) count++;
    return count;
  };

  if (error) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="text-center py-12">
            <div className="text-red-500 text-lg">Error: {error}</div>
            <div className="text-section-muted text-sm mt-2">
              Please try refreshing the page
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const seoTitle = searchQuery
    ? `Search: ${searchQuery}`
    : categoryBreadcrumb.length > 0
      ? categoryBreadcrumb[categoryBreadcrumb.length - 1].name
      : "Shop All Perfumes";
  const seoDescription = searchQuery
    ? `Search results for "${searchQuery}" at My Perfume.`
    : "Browse our full collection of luxury perfumes, oud, and fragrances. Filter by brand, price, and category.";

  return (
    <div className="min-h-screen overflow-x-hidden">
      <SEO title={seoTitle} description={seoDescription} canonicalPath="/products" />
      <Header />

      <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 py-3 sm:py-6">
        <div className="flex flex-col lg:flex-row gap-3 sm:gap-6">
          {/* Desktop Filter Sidebar */}
          <div className="hidden lg:block lg:w-64 xl:w-72 shrink-0">
            <div className="sticky top-24">
              <ProductFilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                availableBrands={availableBrands}
                availableProductTypes={availableProductTypes}
                priceMin={priceMin}
                priceMax={priceMax}
                onClearAll={handleClearAllFilters}
              />
            </div>
          </div>

          {/* Mobile Filter Drawer */}
          <MobileFilterDrawer
            isOpen={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
          >
            <ProductFilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              availableBrands={availableBrands}
                availableProductTypes={availableProductTypes}
              priceMin={priceMin}
              priceMax={priceMax}
              onClearAll={handleClearAllFilters}
              isMobile={true}
              onClose={() => setIsFilterDrawerOpen(false)}
            />
          </MobileFilterDrawer>

          <div className="flex-1 overflow-x-hidden">
            {/* Product Header */}
            <ProductHeader
              totalResults={totalResults}
              resultsPerPage={PAGE_SIZE}
              currentPage={1}
              loadedCount={products.length}
              rangeStart={isSearching ? 0 : infiniteResults.firstPageOffset}
              sortBy={sortBy}
              viewMode={viewMode}
              loading={loading}
              searchQuery={searchQuery}
              categoryBreadcrumb={categoryBreadcrumb}
              onSortChange={handleSortChange}
              onViewModeChange={handleViewModeChange}
              onClearSearch={handleClearSearch}
              onFilterToggle={() => setIsFilterDrawerOpen(true)}
              filterCount={getActiveFilterCount()}
            />

            {/* Active Filters Display */}
            <ActiveFilters
              filters={filters}
              onRemoveFilter={handleRemoveFilter}
              priceMin={priceMin}
              priceMax={priceMax}
            />

            {/* ── Load Previous trigger ──────────────────────────── */}
            {!isSearching && infiniteResults.hasPreviousPage && (
              <div className="py-4 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => infiniteResults.fetchPreviousPage()}
                  disabled={infiniteResults.isFetchingPreviousPage}
                  className="text-section-muted text-sm hover:underline disabled:cursor-default disabled:no-underline flex items-center space-x-2 bg-transparent border-0 p-0"
                >
                  {infiniteResults.isFetchingPreviousPage ? (
                    <>
                      <span className="animate-spin rounded-full h-3 w-3 border-b border-section-muted" />
                      <span>Loading previous products…</span>
                    </>
                  ) : (
                    <span>Load previous products</span>
                  )}
                </button>
              </div>
            )}

            {/* Products Grid */}
            <div className="w-full overflow-hidden">
              <ProductGrid
                products={products}
                viewMode={viewMode}
                loading={loading}
                filtering={false}
              />
            </div>

            {/* ── Load More trigger ──────────────────────────────── */}
            {!isSearching && (
              <div className="py-6 flex items-center justify-center">
                {infiniteResults.hasNextPage ? (
                  <button
                    type="button"
                    onClick={() => infiniteResults.fetchNextPage()}
                    disabled={infiniteResults.isFetchingNextPage}
                    className="text-section-muted text-sm hover:underline disabled:cursor-default disabled:no-underline flex items-center space-x-2 bg-transparent border-0 p-0"
                  >
                    {infiniteResults.isFetchingNextPage ? (
                      <>
                        <span className="animate-spin rounded-full h-3 w-3 border-b border-section-muted" />
                        <span>Loading more products…</span>
                      </>
                    ) : (
                      <span>Load more products</span>
                    )}
                  </button>
                ) : (
                  !infiniteResults.isLoading &&
                  products.length > 0 && (
                    <p className="text-section-muted text-sm">
                      You've seen it all!
                    </p>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ScrollToTopButton />
      <Footer />
    </div>
  );
};

export default DynamicProductPage;
